import React, { ReactElement, useEffect, memo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { isValidUrl } from '@alycecom/utils';

import Footer from '../../../../components/Shared/Footer';
import TangoCard from '../TangoCard/TangoCard';
import { loadRedeemRequest } from '../../store/redeem.actions';
import {
  getIsLoaded,
  getIsLoading,
  getCustomLogo,
  getCredentialPartials,
  getRedemption,
} from '../../store/redeem.selectors';
import AlyceCentredHeader from '../../../../components/AlyceCentredHeader/AlyceCentredHeader';
import spinner from '../../../../assets/images/spinner.svg';
import useRedirect from '../../../../hooks/useRedirect';
import TangoCardInstructions from '../TangoCard/TangoCardInstructions';

const styles: { [className: string]: SxProps<Theme> } = {
  header: {
    fontSize: { xs: 20, sm: 32 },
    fontWeight: 'bold',
    color: ({ palette }) => palette.text.primary,
  },
};

const RedeemPage = (): ReactElement => {
  const dispatch = useDispatch();

  const { hashId } = useParams<{ hashId: string }>();
  const isLoading = useSelector(getIsLoading);
  const isLoaded = useSelector(getIsLoaded);
  const redemption = useSelector(getRedemption);
  const customLogo = useSelector(getCustomLogo);

  const { codes, url } = useSelector(getCredentialPartials);
  const shouldOpenRedemptionUrl = codes.length === 0 && url && isValidUrl(url.value);

  useEffect(() => {
    if (hashId) {
      dispatch(loadRedeemRequest(hashId));
    }
  }, [dispatch, hashId]);

  useRedirect(shouldOpenRedemptionUrl, url?.value, 0);

  return (
    <Box bgcolor="common.white" height="100vh">
      <AlyceCentredHeader image={customLogo} />
      <Box
        className="redeemBlock-ie"
        width={1}
        height={['calc(100vh - 56px)', 'calc(100vh - 80px)']}
        minHeight={['calc(100% - 56px)', 'calc(100% - 80px)']}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        {(isLoading || shouldOpenRedemptionUrl) && (
          <Box
            width={1}
            height={['calc(100vh - 156px)', 'calc(100vh - 180px)']}
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="common.white"
            position="absolute"
          >
            <img src={spinner} alt="Loader" />
          </Box>
        )}
        <Box>
          <Box textAlign="center" mx="auto" mt={[3, 7]} mb={[3, 4]}>
            <Typography sx={styles.header}>How to redeem your gift card</Typography>
          </Box>
          {!isLoading && !shouldOpenRedemptionUrl && isLoaded && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-start"
              width={['100%', 600, 900]}
              mx="auto"
              px={3}
            >
              <TangoCard />
              <TangoCardInstructions content={redemption} />
            </Box>
          )}
        </Box>
        <Footer mobileHidden />
      </Box>
    </Box>
  );
};

export default memo(RedeemPage);
